// ---------------------------------------------------------------------------
// product-configurations.ts — the product configuration table, shared by the browser
// client and GamePlanNodeJsFunctions.
//
// Pure data and pure functions only: no globals, no process.env, no I/O. Each
// host has its own thin accessor that decides WHICH product it is running;
// this module says what each product IS. Keep it that way — the moment this
// file reaches for
// a backing store it stops being importable from both hosts.
//
// The C# copy in src/GamePlanCommon/GamePlanProductConfiguration.cs is a
// parallel implementation of the same shape, guarded by
// product-configuration-csharp-parity.spec.ts. That guard compares member
// NAMES only — it cannot see the values. The two copies can therefore still
// drift silently: change an address or a URL here and the C# keeps serving the
// old one with every test green. Change both, always.
// ---------------------------------------------------------------------------

export interface IGamePlanProductConfiguration {
    productName: string;
    productCode: string;

    accountsEmailAddress: string;
    accountsEmailName: string;
    supportEmailAddress: string;
    supportEmailName: string;
    summaryReportEmailAddress: string;
    summaryReportEmailName: string;
    emailNoReply: string;
    emailAdmin: string;
    emailCompanyDomain: string;

    productWebsiteUrl: string;
    productOnlineHelpUrl: string;
    contactUrl: string;

    logoOnBlackSvgPath: string;
    logo400x80Path: string;
    logoForWhiteBackgroundUrl: string;

    /**
     * Pre-faded watermark artwork for on-screen use, matched in tone to what the
     * PNG and Gantt exports stamp on their output but WITHOUT the tagline, which
     * turns to mush at video sizes. The fade is baked into the image, so draw it
     * at FULL opacity. Applying an extra alpha on top is what left the two
     * products at different weights.
     */
    logoWatermarkPath: string;

    /**
     * Filename of the logo bundled into the Functions app's dist/assets, for
     * embedding in generated PDFs. NOT a URL: the PDF renderers read bytes off
     * disk, so logoOnBlackSvgPath / logo400x80Path (web-root-relative, served
     * by the web role) are unusable there. The browser ignores this.
     */
    pdfLogoAssetFile: string;
}

/**
 * The canonical member list, used by the parity test and by the
 * every-member-populated tests. Must stay in step with the interface above —
 * TypeScript cannot derive this at runtime.
 */
export const PRODUCT_CONFIGURATION_MEMBERS = [
    'productName',
    'productCode',
    'accountsEmailAddress',
    'accountsEmailName',
    'supportEmailAddress',
    'supportEmailName',
    'summaryReportEmailAddress',
    'summaryReportEmailName',
    'emailNoReply',
    'emailAdmin',
    'emailCompanyDomain',
    'productWebsiteUrl',
    'productOnlineHelpUrl',
    'contactUrl',
    'logoOnBlackSvgPath',
    'logo400x80Path',
    'logoForWhiteBackgroundUrl',
    'logoWatermarkPath',
    'pdfLogoAssetFile',
] as const;

export type ProductKey = 'GamePlan' | 'BuilderPlan' | 'ProjectRisk';

/**
 * Used when a host's backing store is absent or unrecognised. GamePlan, not
 * BuilderPlan: an unconfigured host is a mistake, and GamePlan is the name the
 * codebase itself carries, so a wrong logo is at least a self-consistent one.
 */
export const DEFAULT_PRODUCT: ProductKey = 'GamePlan';

const BUILDERPLAN: IGamePlanProductConfiguration = {
    productName: 'BuilderPlan',
    productCode: 'builderplan',
    accountsEmailAddress: 'accounts@builderplan.com',
    accountsEmailName: 'BuilderPlan Accounts Dept.',
    supportEmailAddress: 'support@builderplan.com',
    supportEmailName: 'BuilderPlan Support',
    summaryReportEmailAddress: 'summary@builderplan.com',
    summaryReportEmailName: 'BuilderPlan Notifications',
    emailNoReply: 'noreply@builderplan.com',
    emailAdmin: 'admin@builderplan.com',
    emailCompanyDomain: 'builderplan.com',
    // Bare host, deliberately, even though builderplan.com 301s to www: these
    // links are clicked in-app and in email, never crawled, so the site's
    // canonical form buys nothing here and one convention is easier to keep
    // right across the table. Don't "fix" this to www.
    productWebsiteUrl: 'https://builderplan.com/',
    // BuilderPlan's own docs. Pointed at gameplan.global until both brands
    // published the knowledge base under their own name, which sent BuilderPlan
    // customers to a GamePlan-branded site. Don't send them back there.
    productOnlineHelpUrl: 'https://builderplan.com/docs/',
    contactUrl: 'https://builderplan.com/contact/',
    logoOnBlackSvgPath: '/Content/images/builderplan-logo-with-name-onblack.svg',
    logo400x80Path: '/Content/images/builderplan-logo-with-name-400x80.png',
    logoForWhiteBackgroundUrl: 'https://builderplan.com/assets/brand/builderplan-logo.svg',
    logoWatermarkPath: '/Content/images/builderplan-logo-with-name-watermark.png',
    pdfLogoAssetFile: 'BuilderPlan-logo.jpg',
};

export const PRODUCT_CONFIGURATIONS: Record<ProductKey, IGamePlanProductConfiguration> = {
    GamePlan: {
        productName: 'GamePlan',
        productCode: 'gameplan',
        accountsEmailAddress: 'accounts@gameplan.global',
        accountsEmailName: 'GamePlan Accounts Dept.',
        supportEmailAddress: 'support@gameplan.global',
        supportEmailName: 'GamePlan Support',
        summaryReportEmailAddress: 'summary@gameplan.global',
        summaryReportEmailName: 'GamePlan Notifications',
        emailNoReply: 'noreply@gameplan.global',
        emailAdmin: 'admin@gameplan.global',
        emailCompanyDomain: 'gameplan.global',
        productWebsiteUrl: 'https://gameplan.global/',
        productOnlineHelpUrl: 'https://gameplan.global/docs/',
        contactUrl: 'https://gameplan.global/contact/',
        logoOnBlackSvgPath: '/Content/images/gameplan-logo-with-name-thick-onblack.svg',
        logo400x80Path: '/Content/images/gameplan-logo-with-name-400x80.png',
        logoForWhiteBackgroundUrl: 'https://gameplan.global/assets/brand/gameplan-logo.png',
        logoWatermarkPath: '/Content/images/gameplan-logo-with-name-watermark.png',
        pdfLogoAssetFile: 'GamePlan-logo.png',
    },
    BuilderPlan: BUILDERPLAN,
    // The standalone MPP/XER funnel behind project-risk.com. Derived from
    // BuilderPlan rather than restated: it deliberately carries BuilderPlan's
    // name, logo and email identity — a reader who uploaded a schedule to
    // project-risk.com has not heard of either product — so the two must not
    // be able to drift. Only the website is its own.
    ProjectRisk: {
        ...BUILDERPLAN,
        productWebsiteUrl: 'https://project-risk.com/',
    },
};

/** Narrow an untrusted or absent value to a product key. Null means "not a product". */
export function toProductKey(raw: unknown): ProductKey | null {
    return raw === 'GamePlan' || raw === 'BuilderPlan' || raw === 'ProjectRisk' ? raw : null;
}

/**
 * Members the local-development overlay prefixes. Mirrors the C# #else branch:
 * productName and every email address/name, so a developer can tell local mail
 * from real mail at a glance. productCode is NOT prefixed — it is compared
 * against literals in get-started-dialog.ts and plan-reveal-video-export.ts.
 */
const LOCAL_PREFIXED_MEMBERS = [
    'productName',
    'accountsEmailAddress',
    'accountsEmailName',
    'supportEmailAddress',
    'supportEmailName',
    'summaryReportEmailAddress',
    'summaryReportEmailName',
    'emailNoReply',
    'emailAdmin',
] as const;

const LOCAL_PREFIX = 'LOCAL.';

/** Environment overlay, not a product — avoids a product x environment row set. */
export function withLocalPrefix(
    config: IGamePlanProductConfiguration,
): IGamePlanProductConfiguration {
    const result = { ...config };
    for (const member of LOCAL_PREFIXED_MEMBERS) {
        result[member] = LOCAL_PREFIX + result[member];
    }
    return result;
}

/**
 * Bare host for display — "gameplan.global", "project-risk.com". Derived from
 * productWebsiteUrl rather than stored as a 19th member, so the two can never
 * disagree. Used on the Risk Report PDF cover and the Executive Summary footer.
 */
export function productWebsiteHost(config: IGamePlanProductConfiguration): string {
    return config.productWebsiteUrl
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .replace(/\/.*$/, '');
}
